import { createContext, useContext, useEffect } from 'react';

import { usePathname } from 'next/navigation';

import { track, trackPageView } from '@/lib/analytics';

type AnalyticsContextType = {
  track: (eventName: string, properties?: Record<string, any>) => void;
};

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined
);

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    // Skip tracking for less important pages
    const ignoredPaths = [
      '/_error',
      '/_app',
      '/_document',
      '/api',
      '/static',
      '/favicon.ico',
    ];

    if (ignoredPaths.some((path) => pathname?.startsWith(path))) {
      return;
    }

    // Track page view
    trackPageView(pathname || '');
  }, [pathname]);

  return (
    <AnalyticsContext.Provider value={{ track }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
